/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */
export class TapeSlider {
  constructor(container, strip, { min, max, start, step = 0.5, tickWidth = 10, majorEvery = 10, midEvery = 5, tickBaseClass, tickMinorClass, tickMidClass, tickMajorClass, tickExtraClass, getScale, onChange }) {
    this.container = container;
    this.strip = strip;
    this.min = min;
    this.max = max;
    this.value = start;
    this.step = step;
    this.tickWidth = tickWidth;
    this.tickCenter = 1;
    this.majorEvery = majorEvery;
    this.midEvery = midEvery;
    this.tickBaseClass = tickBaseClass;
    this.tickMinorClass = tickMinorClass;
    this.tickMidClass = tickMidClass;
    this.tickMajorClass = tickMajorClass;
    this.tickExtraClass = tickExtraClass;
    this.getScale = getScale || (() => 1);
    this.onChange = onChange;
    this.dragging = false;
    this.startX = 0;
    this.startOffset = 0;
    this.currentOffset = 0;
    this.wheelSnapTimer = null;
    this._onDown = this.onDown.bind(this);
    this._onMove = this.onMove.bind(this);
    this._onUp = this.onUp.bind(this);
    this._onWheel = this.onWheel.bind(this);
    this.initTicks();
    this.setValue(start, true);
    this.container.addEventListener('mousedown', this._onDown);
    this.container.addEventListener('touchstart', this._onDown, { passive: false });
    this.container.addEventListener('wheel', this._onWheel, { passive: false });
    window.addEventListener('mousemove', this._onMove);
    window.addEventListener('touchmove', this._onMove, { passive: false });
    window.addEventListener('mouseup', this._onUp);
    window.addEventListener('touchend', this._onUp);
  }
  destroy() {
    clearTimeout(this.wheelSnapTimer);
    this.container.removeEventListener('mousedown', this._onDown);
    this.container.removeEventListener('touchstart', this._onDown);
    this.container.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('mousemove', this._onMove);
    window.removeEventListener('touchmove', this._onMove);
    window.removeEventListener('mouseup', this._onUp);
    window.removeEventListener('touchend', this._onUp);
  }
  initTicks() {
    this.strip.innerHTML = '';
    const stepsCount = (this.max - this.min) / this.step;
    const roundKey = (n) => Math.round(n * 1000);
    for (let i = 0; i <= stepsCount; i++) {
      const val = this.min + i * this.step;
      const tick = document.createElement('div');
      tick.className = this.tickExtraClass ? `${this.tickBaseClass} ${this.tickExtraClass}` : this.tickBaseClass;
      const isMajor = this.majorEvery ? roundKey(val) % roundKey(this.majorEvery) === 0 : false;
      const isMid = !isMajor && this.midEvery ? roundKey(val) % roundKey(this.midEvery) === 0 : false;
      if (isMajor) {
        tick.classList.add(this.tickMajorClass);
        const label = document.createElement('span');
        label.textContent = Number.isInteger(this.majorEvery) ? val.toFixed(0) : val.toFixed(1);
        tick.appendChild(label);
      } else if (isMid) {
        tick.classList.add(this.tickMidClass);
      } else {
        tick.classList.add(this.tickMinorClass);
      }
      this.strip.appendChild(tick);
    }
  }
  setValue(val, triggerCallback = true) {
    this.value = Math.min(this.max, Math.max(this.min, val));
    const stepsFromMin = Math.round((this.value - this.min) / this.step);
    this.value = this.min + stepsFromMin * this.step;
    const pillWidth = this.container.clientWidth || 150;
    this.currentOffset = pillWidth / 2 - stepsFromMin * this.tickWidth - this.tickCenter;
    this.strip.style.transform = `translateX(${this.currentOffset}px)`;
    if (triggerCallback && this.onChange) this.onChange(this.value);
  }
  onDown(e) {
    this.dragging = true;
    const p = e.touches ? e.touches[0] : e;
    this.startX = p.clientX;
    this.startOffset = this.currentOffset;
    e.preventDefault();
  }
  onMove(e) {
    if (!this.dragging) return;
    const p = e.touches ? e.touches[0] : e;
    const S = this.getScale();
    const deltaX = (p.clientX - this.startX) / S;
    const liveOffset = this.startOffset + deltaX;
    const pillWidth = this.container.clientWidth || 150;
    const stepsFromMin = -(liveOffset - pillWidth / 2 + this.tickCenter) / this.tickWidth;
    const liveValue = this.min + stepsFromMin * this.step;
    const clampedVal = Math.min(this.max, Math.max(this.min, liveValue));
    this.currentOffset = liveOffset;
    this.strip.style.transform = `translateX(${liveOffset}px)`;
    const snappedVal = this.min + Math.round((clampedVal - this.min) / this.step) * this.step;
    if (this.onChange) this.onChange(snappedVal);
    e.preventDefault();
  }
  onUp() {
    if (!this.dragging) return;
    this.dragging = false;
    const pillWidth = this.container.clientWidth || 150;
    const stepsFromMin = -(this.currentOffset - pillWidth / 2 + this.tickCenter) / this.tickWidth;
    const finalVal = this.min + Math.round(stepsFromMin) * this.step;
    this.setValue(finalVal, true);
  }
  onWheel(e) {
    e.preventDefault();
    let delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (e.deltaMode === 1) delta *= 16;else
    if (e.deltaMode === 2) delta *= this.container.clientWidth;
    this.currentOffset -= delta;
    this.strip.style.transform = `translateX(${this.currentOffset}px)`;
    const pillWidth = this.container.clientWidth || 150;
    const stepsFromMin = -(this.currentOffset - pillWidth / 2 + this.tickCenter) / this.tickWidth;
    const liveValue = this.min + stepsFromMin * this.step;
    const clampedVal = Math.min(this.max, Math.max(this.min, liveValue));
    const snappedVal = this.min + Math.round((clampedVal - this.min) / this.step) * this.step;
    if (this.onChange) this.onChange(snappedVal);
    clearTimeout(this.wheelSnapTimer);
    this.wheelSnapTimer = setTimeout(() => {
      this.wheelSnapTimer = null;
      this.setValue(snappedVal, true);
    }, 120);
  }
}
